import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { cx } from '@/lib/clases'
import { BORDE_IZQUIERDO, type Tono } from './tonos'

/**
 * Notificación discreta abajo a la derecha, **persistente hasta leerse**.
 *
 * Es donde va el error de fondo: la cola, el envío, una entrega que falló
 * mientras el usuario estaba en otra pantalla. No interrumpe y no bloquea.
 *
 * Y la regla que evita el ruido: **un "aceptado" no merece notificación, es lo
 * esperado.** Solo se avisa de lo que exige acción.
 */

export interface Aviso {
  id: string
  tono: Tono
  titulo: string
  detalle?: string
  /** Enlace o botón que lleva al lugar donde se resuelve. */
  accion?: ReactNode
}

interface ContextoAvisos {
  avisos: readonly Aviso[]
  publicar: (aviso: Omit<Aviso, 'id'>) => string
  descartar: (id: string) => void
}

const Contexto = createContext<ContextoAvisos | null>(null)

export function ProveedorAvisos({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<readonly Aviso[]>([])

  const descartar = useCallback((id: string) => {
    setAvisos((lista) => lista.filter((a) => a.id !== id))
  }, [])

  const publicar = useCallback((aviso: Omit<Aviso, 'id'>) => {
    const id = `aviso-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    setAvisos((lista) => [...lista, { ...aviso, id }])
    return id
  }, [])

  const valor = useMemo(() => ({ avisos, publicar, descartar }), [avisos, publicar, descartar])

  return (
    <Contexto.Provider value={valor}>
      {children}
      <PilaDeAvisos avisos={avisos} onDescartar={descartar} />
    </Contexto.Provider>
  )
}

export function useAvisos(): ContextoAvisos {
  const contexto = useContext(Contexto)
  if (contexto === null) {
    throw new Error('useAvisos necesita <ProveedorAvisos> por encima.')
  }
  return contexto
}

export function PilaDeAvisos({
  avisos,
  onDescartar,
}: {
  avisos: readonly Aviso[]
  onDescartar: (id: string) => void
}) {
  if (avisos.length === 0) return null

  return (
    <div
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[var(--capa-aviso)] flex w-[340px] flex-col gap-2 sin-impresion"
    >
      {avisos.map((aviso) => (
        <div
          key={aviso.id}
          className={cx(
            'flex items-start gap-2 rounded-md border border-gris-300 border-l-[3px] bg-white p-3',
            'shadow-flotante',
            BORDE_IZQUIERDO[aviso.tono],
          )}
        >
          <div className="min-w-0 flex-1">
            <p className="text-cuerpo font-semibold text-gris-900">{aviso.titulo}</p>
            {aviso.detalle ? <p className="text-etiqueta text-gris-600">{aviso.detalle}</p> : null}
            {aviso.accion ? <div className="mt-2">{aviso.accion}</div> : null}
          </div>
          <button
            type="button"
            aria-label={`Descartar aviso: ${aviso.titulo}`}
            onClick={() => onDescartar(aviso.id)}
            className="shrink-0 rounded-md p-1 text-gris-600 hover:bg-gris-100 hover:text-gris-900"
          >
            <X aria-hidden="true" className="size-4" />
          </button>
        </div>
      ))}
    </div>
  )
}
